import type { TipoCampo } from '../../../api/ops/clientesCatalogo';
import type { NombreIcono } from '../../../componentes/iconos';
import type { TarjetaAyuda } from '../../../marco/cabecera';

/**
 * Textos de «Etiquetas y campos propios» (2.8), tal cual del diseño (`2.8-etiquetas-y-campos.html`) salvo donde una
 * decisión lo corrige: G192 (fuera «Alergias» de los ejemplos y las ayudas: ningún campo, etiqueta o nota debe recoger
 * salud) y G140 (la única cifra es `Tag.customer_count`; «Archivadas» sin cifra).
 */
export const PERMISO_CATALOGO = 'clientes.catalogo.gestionar';

/** Colores de etiqueta: son datos (`Tag.color`, #RRGGBB), no tokens del panel. */
export const COLORES: readonly [string, string][] = [
    ['#DD1420', 'Rojo'],
    ['#111111', 'Negro'],
    ['#F2B600', 'Amarillo'],
    ['#E8730C', 'Naranja'],
    ['#1F9D55', 'Verde'],
    ['#2F6FDE', 'Azul'],
    ['#8A4FD8', 'Morado'],
    ['#8C8C88', 'Gris'],
];

export const TIPOS: readonly { tipo: TipoCampo; icono: NombreIcono; nombre: string; ejemplo: string }[] = [
    // G192: sin «Alergias» en los ejemplos.
    { tipo: 'text', icono: 'type', nombre: 'Texto', ejemplo: 'Empresa, comentarios…' },
    { tipo: 'number', icono: 'hash', nombre: 'Número', ejemplo: 'Número de socio, comensales…' },
    { tipo: 'date', icono: 'calendar', nombre: 'Fecha', ejemplo: 'Aniversario, alta en el club…' },
    { tipo: 'boolean', icono: 'toggle-left', nombre: 'Sí o no', ejemplo: 'Quiere factura, es socio…' },
    { tipo: 'select', icono: 'list', nombre: 'Una opción', ejemplo: 'Elige una de una lista.' },
    { tipo: 'multiselect', icono: 'list-checks', nombre: 'Varias opciones', ejemplo: 'Marca varias de una lista.' },
];

export const TIPO: Readonly<Record<TipoCampo, (typeof TIPOS)[number]>> = Object.fromEntries(TIPOS.map((t) => [t.tipo, t])) as Record<TipoCampo, (typeof TIPOS)[number]>;

export const AYUDA: Record<'etiquetas' | 'campos' | 'etiqueta' | 'campo', TarjetaAyuda[]> = {
    etiquetas: [
        { icono: 'tags', titulo: 'Etiquetas', texto: 'Agrupan clientes: VIP, Habitual, Empresa… Las pones en la ficha de cada cliente y filtras la lista con ellas.' },
        { icono: 'archive', titulo: 'Archivar', texto: 'Una etiqueta archivada deja de ofrecerse y de verse, pero nadie la pierde: nada se borra.' },
        { icono: 'undo-2', titulo: 'Deshacer', texto: 'Archivar se aplica al momento y tienes 5 segundos para deshacerlo.' },
    ],
    campos: [
        // G192: sin «alergias» en la ayuda.
        { icono: 'contact', titulo: 'Campos propios', texto: 'Lo que quieres saber de cada cliente y Reservaria no pide: empresa, número de socio, mesa preferida…' },
        { icono: 'list', titulo: 'Tipo', texto: 'Texto, número, fecha, sí o no, o una lista de opciones. Si cambias el tipo de un campo con valores, se convierten; si alguno no puede, no cambia nada.' },
        { icono: 'archive', titulo: 'Archivar', texto: 'Un campo archivado deja de pedirse y de verse en la ficha. Sus valores se guardan.' },
    ],
    etiqueta: [{ icono: 'tags', titulo: 'Nombre y color', texto: 'El nombre no se puede repetir entre las etiquetas activas. El color ayuda a reconocerla en la lista y en la ficha.' }],
    campo: [
        { icono: 'key-round', titulo: 'Clave', texto: 'La usan las integraciones y la importación por CSV. Se elige al crear el campo y no cambia.' },
        { icono: 'shield-check', titulo: 'Obligatorio', texto: 'Solo se pide en el panel. Los clientes que ya tienes no se ven afectados hasta que edites su ficha.' },
    ],
};

export const VACIA = {
    etiquetas: { titulo: 'Todavía no hay etiquetas.', texto: 'Crea la primera para agrupar clientes y filtrar la lista: VIP, Habitual, Empresa…' },
    // G192: sin «alergias» en el texto de vacío.
    campos: { titulo: 'Todavía no hay campos propios.', texto: 'Crea uno para guardar lo que tu negocio necesita saber de cada cliente: empresa, número de socio, mesa preferida…' },
} as const;

export const TEXTOS = {
    soloLectura: 'El negocio está en solo lectura: puedes consultar, pero no crear, cambiar ni archivar.',
    archivadasNota: 'Las archivadas no se ofrecen ni se ven en las fichas, pero quien la tenía la conserva. Si la necesitas otra vez, recupérala.',
    archivadosNota: 'Los campos archivados no se piden ni se ven, pero sus valores se guardan hasta que se anonimice cada cliente. Si lo necesitas otra vez, recupéralo con sus valores.',
    archivarEtiquetaNota: 'Deja de ofrecerse y de verse en las fichas. Nadie la pierde y tienes 5 segundos para deshacerlo.',
    archivarCampoConValores: 'Este campo ya tiene valores en algunos clientes: se guardan y vuelven si lo recuperas. Deja de pedirse y de verse en las fichas; tienes 5 segundos para deshacerlo.',
    archivarCampoSinValores: 'Deja de pedirse y de verse en las fichas. Ningún cliente tiene valor en él. Tienes 5 segundos para deshacerlo.',
    conflictoNota: 'No he cambiado nada. Corrige esos valores en la ficha de cada cliente, vuelve al tipo anterior o archiva este campo y crea otro.',
    opcionesNota: 'Si quitas una opción que ya tiene algún cliente, te pediré convertir sus valores antes de guardar.',
    obligatorioAyuda: 'Se pide al dar de alta o editar un cliente en el panel.',
} as const;

export function clientesDe(n: number | undefined): string {
    const v = n ?? 0;
    return v === 1 ? '1 cliente' : `${String(v)} clientes`;
}

/** La clave propuesta a partir del nombre («Mesa preferida» → `mesa_preferida`). */
export function claveDe(nombre: string): string {
    return nombre
        .toLocaleLowerCase('es')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .replace(/^([0-9])/, 'c_$1')
        .slice(0, 40);
}

/** ¿Se pinta en negro sobre ese color? (los claros, como el amarillo). */
export function esClaro(hex: string): boolean {
    const n = parseInt(hex.slice(1), 16);
    return (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255 > 0.6;
}
